/**
 * Push to mytender: one verdict per question, then the sheet becomes its own
 * receipt. Rows warn when someone has the question open in mytender right
 * now (their typing merges with the push) and when io.ai suggestions pending
 * there will be discarded. If io.ai is editing the document, the push waits
 * for it to finish. A failed question keeps a Retry; the others stay pushed.
 */
import { ArrowLeftIcon, XIcon } from "@/design/icons";
import { type FC, useCallback, useEffect, useRef, useState } from "react";

import { cn } from "@/agents-v2/utils/cn";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/design/ui/sheet";
import { Skeleton } from "@/design/ui/skeleton";

import { describeError } from "../document/errors";
import type { PushResult } from "../tender/questionsApi";
import type { PushPlan, PushPlanRow, QuestionsApi, Verdict } from "../tender/useQuestions";
import { NUMBER_CLASS, numberColumnStyle } from "./numberColumn";
import { statusLabel } from "./StatusMenu";

const VERDICT: Record<Verdict, { label: string; tone: string }> = {
  unchanged: { label: "Unchanged", tone: "text-fg-4 font-medium" },
  behind: { label: "Newer in mytender", tone: "text-fg-4 font-medium" },
  changed: { label: "Changed", tone: "text-fg" },
  newer: { label: "Newer in mytender", tone: "text-accent-hover" },
  new: { label: "New", tone: "text-status-complete" },
  skipped: { label: "Skipped", tone: "text-fg-4 font-medium" },
  missing: { label: "Skipped", tone: "text-fg-4 font-medium" },
};

const ago = (iso?: string | null): string => {
  if (!iso) return "";
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 0) return "";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.round(hours / 24)} days ago`;
};

const who = (login?: string | null) =>
  login
    ? login
        .split("@")[0]
        .replace(/[._-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase())
    : "Someone";

const reasonFor = (row: PushPlanRow): { text: string; live?: boolean } => {
  const editors = row.live?.editors ?? [];
  if (row.verdict !== "missing" && row.verdict !== "skipped" && row.verdict !== "unchanged") {
    if (editors.length)
      return {
        text: `${editors.join(", ")} ${editors.length > 1 ? "are" : "is"} editing this in mytender now`,
        live: true,
      };
    if ((row.live?.pending_suggestions ?? 0) > 0) {
      const n = row.live?.pending_suggestions ?? 0;
      return { text: `${n} io.ai suggestion${n === 1 ? "" : "s"} in mytender will be discarded` };
    }
  }
  switch (row.verdict) {
    case "missing":
      return { text: "Not in document" };
    case "skipped":
      return { text: "No text in the document" };
    case "unchanged":
      return { text: "Same as mytender" };
    case "new":
      return { text: "No answer in mytender yet" };
    case "behind": {
      const when = ago(row.question.last_edited_at);
      return {
        text: `Not edited here; ${who(row.question.last_edited_by)} changed it in mytender${when ? ` ${when}` : ""}. Get from mytender to update this document.`,
      };
    }
    case "newer": {
      const when = ago(row.question.last_edited_at);
      return { text: `${who(row.question.last_edited_by)} edited it in mytender${when ? ` ${when}` : ""}` };
    }
    default: {
      const bits: string[] = [];
      const pc = row.content?.pendingChanges ?? 0;
      const figs = row.content?.figures ?? 0;
      if (pc) bits.push(`${pc} tracked change${pc === 1 ? "" : "s"} sent as accepted`);
      if (figs) bits.push(`${figs} figure${figs === 1 ? "" : "s"} sent as a placeholder`);
      return { text: bits.join(" · ") || "Updated in the document" };
    }
  }
};

export type PushSheetProps = {
  open: boolean;
  sectionIds?: string[];
  q: QuestionsApi;
  agentRunning: boolean;
  onClose: () => void;
  onPullAll: () => void;
};

type Phase = "planning" | "review" | "pushing" | "done" | "error";

export const PushSheet: FC<PushSheetProps> = ({ open, sectionIds, q, agentRunning, onClose, onPullAll }) => (
  <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
    <SheetContent side="right" className="w-full max-w-none gap-0 p-0 sm:max-w-none [&>button]:hidden">
      {open && (
        <Body sectionIds={sectionIds} q={q} agentRunning={agentRunning} onClose={onClose} onPullAll={onPullAll} />
      )}
    </SheetContent>
  </Sheet>
);

const Body: FC<Omit<PushSheetProps, "open">> = ({ sectionIds, q, agentRunning, onClose, onPullAll }) => {
  const [phase, setPhase] = useState<Phase>("planning");
  const [plan, setPlan] = useState<PushPlan | null>(null);
  const [results, setResults] = useState<PushResult[]>([]);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const planned = useRef(false);
  const numberStyle = numberColumnStyle(q.questions.map((x) => x.section_number));

  useEffect(() => {
    if (planned.current) return;
    planned.current = true;
    q.planPush(sectionIds)
      .then((p) => {
        setPlan(p);
        setPhase("review");
      })
      .catch((e) => {
        // Word's code and location, so a refusal can be traced (not just "GeneralException").
        setError(`Couldn't read the document: ${describeError(e)}`);
        setPhase("error");
      });
  }, [q, sectionIds]);

  const run = useCallback(
    async (rows: PushPlanRow[]) => {
      setPhase("pushing");
      const out = await q.push(rows);
      setResults((prev) => {
        const merged = new Map(prev.map((r) => [r.section_id, r]));
        for (const r of out.results) merged.set(r.section_id, r);
        return [...merged.values()];
      });
      setPhase("done");
    },
    [q]
  );

  // Push once io.ai stops editing the document.
  useEffect(() => {
    if (waiting && !agentRunning && plan) {
      setWaiting(false);
      void run(plan.rows);
    }
  }, [waiting, agentRunning, plan, run]);

  const toPush = plan?.rows.filter((r) => r.include) ?? [];
  const title =
    phase === "done" ? receiptTitle(results) : `Send ${toPush.length} question${toPush.length === 1 ? "" : "s"}`;

  return (
    <div className="flex h-full flex-col bg-surface">
      <div className="flex h-11 shrink-0 items-center gap-1 border-b border-line px-2">
        <button
          type="button"
          aria-label={phase === "done" ? "Close" : "Back"}
          onClick={onClose}
          className="flex size-7 items-center justify-center rounded-sm text-fg-2 hover:bg-subtle hover:text-fg"
        >
          {phase === "done" ? <XIcon className="size-4" /> : <ArrowLeftIcon className="size-4" />}
        </button>
        <span className="text-sm font-medium text-fg-3">Send to mytender</span>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="px-4 pb-2 pt-4">
          <SheetTitle className="text-lg font-semibold leading-snug text-fg">
            {phase === "planning" ? "Checking the document…" : title}
          </SheetTitle>
          <SheetDescription className="mt-0.5 text-sm text-fg-3">
            {phase === "done"
              ? results.some((r) => r.status === "failed")
                ? "Some questions couldn't be sent. The others are saved."
                : "Earlier versions are in each question's history in mytender."
              : "Each answer becomes the current version in mytender; earlier versions stay in history."}
          </SheetDescription>
        </div>

        {phase === "review" && plan?.stale && (
          <div className="mx-3 mb-2 rounded-sm border border-line bg-subtle px-2.5 py-2 text-xs leading-[17px] text-fg-2">
            Last sent from this document {ago(plan.lastPushAt)}. {plan.rows.filter((r) => r.verdict === "newer").length}{" "}
            answers are newer in mytender since then. Get from mytender first to bring them in, or send to replace them.
          </div>
        )}

        {phase === "planning" && (
          <div className="flex flex-col gap-3 px-4 py-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex flex-col gap-1.5">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))}
          </div>
        )}

        {phase === "error" && (
          <div className="mx-3 rounded-sm border border-danger/30 bg-danger-soft px-2.5 py-2 text-xs text-danger">
            {error}
          </div>
        )}

        {(phase === "review" || phase === "pushing") &&
          plan?.rows.map((row, i) => {
            const reason = reasonFor(row);
            const v = VERDICT[row.verdict];
            return (
              <div
                key={row.question.section_id}
                className={cn("flex items-start gap-2.5 px-3 py-2", i && "border-t border-line/70")}
              >
                <span className={cn(NUMBER_CLASS, "pt-px")} style={numberStyle}>
                  {row.question.section_number}
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn("block truncate font-medium", row.verdict === "missing" && "text-fg-3")}>
                    {row.question.heading || "Untitled question"}
                  </span>
                  <span className="flex items-center gap-1.5 text-xs text-fg-3">
                    {reason.live && (
                      <span className="size-[7px] shrink-0 rounded-full bg-status-complete shadow-[0_0_0_3px_rgba(75,148,107,0.2)]" />
                    )}
                    <span className="truncate">{reason.text}</span>
                  </span>
                </span>
                <span className={cn("shrink-0 pt-px text-xs font-semibold", v.tone)}>{v.label}</span>
              </div>
            );
          })}

        {phase === "done" && (
          <>
            {results.map((r, i) => {
              const question = q.questions.find((x) => x.section_id === r.section_id);
              const failed = r.status === "failed";
              return (
                <div
                  key={r.section_id}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2",
                    i && "border-t border-line/70",
                    failed && "bg-danger-soft"
                  )}
                >
                  <span className={NUMBER_CLASS} style={numberStyle}>
                    {question?.section_number}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{question?.heading ?? "Question"}</span>
                    <span className={cn("block truncate text-xs", failed ? "text-danger" : "text-fg-3")}>
                      {failed
                        ? friendlyReason(r.reason)
                        : r.status === "skipped"
                          ? "No text to send"
                          : r.status_changed_to
                            ? `New version · Not started → ${statusLabel(r.status_changed_to)}`
                            : "New version · status unchanged"}
                    </span>
                  </span>
                  {failed ? (
                    <button
                      type="button"
                      className="shrink-0 rounded-sm bg-fg px-2.5 py-1 text-xs font-semibold text-fg-inverse"
                      onClick={() =>
                        plan &&
                        void run(
                          plan.rows
                            .filter((p) => p.question.section_id === r.section_id)
                            .map((p) => ({ ...p, include: true }))
                        )
                      }
                    >
                      Retry
                    </button>
                  ) : (
                    <span
                      className={cn(
                        "shrink-0 text-xs font-semibold",
                        r.status === "pushed" ? "text-status-complete" : "text-fg-4"
                      )}
                    >
                      {r.status === "pushed" ? "Sent" : "Skipped"}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-line px-3 pb-3 pt-2.5">
        {phase === "review" && (
          <p className="mb-2 text-xs leading-[17px] text-fg-3">
            {agentRunning
              ? "io.ai is editing the document. The send starts when it finishes, with the text as it is then. Keep this sheet open."
              : "Comments aren't sent. Questions still Not started move to In progress; every other status stays as it is."}
          </p>
        )}
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs tabular-nums text-fg-2">{footerCount(phase, plan, results)}</span>
          {phase === "review" && plan?.stale && (
            <button
              type="button"
              onClick={onPullAll}
              className="rounded-md border border-line-strong px-3 py-1.5 text-sm font-semibold text-fg hover:bg-subtle"
            >
              Get from mytender
            </button>
          )}
          {phase === "review" &&
            (waiting ? (
              <span className="inline-flex items-center gap-2 rounded-md border border-line bg-subtle px-3.5 py-1.5 text-sm font-semibold text-fg-2">
                <span className="size-3 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
                Waiting for io.ai
              </span>
            ) : (
              <button
                type="button"
                disabled={!toPush.length}
                onClick={() => (agentRunning ? setWaiting(true) : plan && void run(plan.rows))}
                className="rounded-md bg-accent px-3.5 py-1.5 text-sm font-semibold text-fg-inverse hover:bg-accent-hover disabled:opacity-40"
              >
                Send {toPush.length}
              </button>
            ))}
          {phase === "pushing" && (
            <span className="inline-flex items-center gap-2 rounded-md border border-line bg-subtle px-3.5 py-1.5 text-sm font-semibold text-fg-2">
              <span className="size-3 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
              Sending…
            </span>
          )}
          {phase === "done" &&
            (results.some((r) => r.status === "failed") && plan ? (
              <button
                type="button"
                onClick={() =>
                  void run(
                    plan.rows
                      .filter((p) =>
                        results.some((r) => r.section_id === p.question.section_id && r.status === "failed")
                      )
                      .map((p) => ({ ...p, include: true }))
                  )
                }
                className="rounded-md bg-accent px-3.5 py-1.5 text-sm font-semibold text-fg-inverse hover:bg-accent-hover"
              >
                Retry {results.filter((r) => r.status === "failed").length}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-accent px-3.5 py-1.5 text-sm font-semibold text-fg-inverse hover:bg-accent-hover"
              >
                Done
              </button>
            ))}
        </div>
      </div>
    </div>
  );
};

const friendlyReason = (reason?: string) =>
  !reason
    ? "Couldn't be sent"
    : /403|forbidden|access/i.test(reason)
      ? "You don't have edit access to this bid"
      : /network|timeout|unreach|collab/i.test(reason)
        ? "Connection lost during the send"
        : "Couldn't be sent";

const receiptTitle = (results: PushResult[]) => {
  const pushed = results.filter((r) => r.status === "pushed").length;
  const failed = results.filter((r) => r.status === "failed").length;
  return failed ? `Sent ${pushed} of ${pushed + failed}` : `Sent ${pushed} question${pushed === 1 ? "" : "s"}`;
};

const footerCount = (phase: Phase, plan: PushPlan | null, results: PushResult[]) => {
  if (phase === "done") {
    const pushed = results.filter((r) => r.status === "pushed").length;
    const failed = results.filter((r) => r.status === "failed").length;
    return [`${pushed} sent`, failed ? `${failed} failed` : null].filter(Boolean).join(" · ");
  }
  if (!plan) return "";
  const push = plan.rows.filter((r) => r.include).length;
  const skipped = plan.rows.filter((r) => r.verdict === "missing" || r.verdict === "skipped").length;
  const unchanged = plan.rows.filter((r) => r.verdict === "unchanged").length;
  return [`${push} to send`, unchanged ? `${unchanged} unchanged` : null, skipped ? `${skipped} skipped` : null]
    .filter(Boolean)
    .join(" · ");
};
